import { CartAddEvent } from '@theme/events';

class OmerellaProductFloatingVideo extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {HTMLVideoElement | null} */
  #preview = null;

  /** @type {HTMLElement | null} */
  #bubble = null;

  /** @type {HTMLElement | null} */
  #progressBar = null;

  /** @type {object | null} */
  #reel = null;

  /** @type {(() => void) | null} */
  #onTimeUpdate = null;

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.removeAttribute('hidden');
    this.#mountToBody();

    if (this.#abort) {
      this.#preview = this.querySelector('.omerella-product-float__preview');
      this.#bubble = this.querySelector('[data-float-bubble]');
      this.#bindDialog();
      this.#setupPreviewAutoplay();
      return;
    }

    this.#preview = this.querySelector('.omerella-product-float__preview');
    this.#bubble = this.querySelector('[data-float-bubble]');
    this.#reel = this.#readReelData();
    this.#bubble?.classList.remove('is-dismissed');

    this.#bindDialog();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.querySelector('.omerella-product-float__open')?.addEventListener('click', () => this.#open(), { signal });
    this.querySelector('.omerella-product-float__dismiss')?.addEventListener(
      'click',
      (e) => {
        e.stopPropagation();
        this.#dismissBubble();
      },
      { signal }
    );

    this.#dialog?.addEventListener('click', this.#handleDialogClick, { signal });

    this.#onTimeUpdate = () => this.#updateProgress();
    this.#video?.addEventListener('timeupdate', this.#onTimeUpdate, { signal });

    this.#dialog?.addEventListener(
      'close',
      () => {
        this.#stopModalVideo();
        this.#resumePreview();
      },
      { signal }
    );

    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('variant:update', this.#onVariantUpdate, { signal });

    window.addEventListener(
      'pageshow',
      () => {
        document.documentElement.classList.remove('omerella-reel-modal-open');
        this.#dialog?.close();
      },
      { signal }
    );

    this.#setupPreviewAutoplay();
    this.#loadReelData();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#dialog?.close();
    document.documentElement.classList.remove('omerella-reel-modal-open');

    if (this.#dialog?.parentElement === document.body && this.isConnected) {
      this.appendChild(this.#dialog);
    }
  }

  #mountToBody() {
    const productId = this.dataset.productId || '0';

    document
      .querySelectorAll(`omerella-product-floating-video[data-product-id="${productId}"]`)
      .forEach((element) => {
        if (element !== this) element.remove();
      });

    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }
  }

  #readReelData() {
    const dataEl = this.querySelector('[data-reel-json]');
    const raw =
      dataEl instanceof HTMLTemplateElement
        ? dataEl.innerHTML.trim()
        : dataEl?.textContent?.trim() || '';

    if (raw) {
      try {
        return JSON.parse(raw);
      } catch {
        /* fall through to dataset fallback */
      }
    }

    const mp4 = this.dataset.videoMp4;
    if (!mp4) return null;

    return {
      mp4,
      sources: [{ url: mp4, type: 'video/mp4' }],
      variantId: '',
      productId: this.dataset.productId || '',
      canAddToCart: true,
      likes: 0,
      shares: 0,
    };
  }

  #bindDialog() {
    const ownerId = this.dataset.productId || '0';
    this.#dialog = this.querySelector('.omerella-reel-modal');

    if (!this.#dialog) {
      this.#dialog = document.body.querySelector(`dialog.omerella-reel-modal--product[data-owner="${ownerId}"]`);
      if (this.#dialog) {
        this.appendChild(this.#dialog);
      }
    }

    if (!this.#dialog) return false;

    this.#dialog.classList.add('omerella-reel-modal--product');
    this.#dialog.dataset.owner = ownerId;

    this.#video = this.#dialog.querySelector('.omerella-reel-modal__video');
    this.#progressBar = this.#dialog.querySelector('.omerella-reel-modal__progress-bar');
    return Boolean(this.#video);
  }

  #portalDialog() {
    if (this.#dialog && this.#dialog.parentElement !== document.body) {
      document.body.appendChild(this.#dialog);
    }
  }

  /** @param {Event} event */
  #handleDialogClick = (event) => {
    if (!this.#dialog?.open) return;

    const target = /** @type {HTMLElement} */ (event.target);
    if (!target) return;

    if (target.closest('.omerella-reel-modal__close') || target.closest('.omerella-reel-modal__backdrop')) {
      event.preventDefault();
      event.stopPropagation();
      this.#close();
      return;
    }

    if (target.closest('.omerella-reel-modal__mute')) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleMute();
      return;
    }

    const cartBtn = target.closest('.omerella-reel-modal__cart');
    if (cartBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#addToCart(event, cartBtn);
      return;
    }

    if (target.closest('.omerella-reel-modal__whatsapp')) {
      event.preventDefault();
      event.stopPropagation();
      this.#openWhatsApp();
      return;
    }

    if (target.closest('.omerella-reel-modal__share-btn')) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggleShare();
      return;
    }

    if (target.closest('.omerella-reel-modal__share-whatsapp')) {
      event.preventDefault();
      event.stopPropagation();
      this.#shareWhatsApp();
      return;
    }

    if (target.closest('.omerella-reel-modal__share-copy')) {
      event.preventDefault();
      event.stopPropagation();
      this.#copyLink();
      return;
    }

    if (target.closest('.omerella-reel-modal__share-more')) {
      event.preventDefault();
      event.stopPropagation();
      this.#shareNative();
      return;
    }

    const likeBtn = target.closest('.omerella-reel-modal__like');
    if (likeBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      likeBtn.classList.toggle('is-liked');
      const countEl = likeBtn.querySelector('[data-like-count]');
      if (!countEl) return;
      const base = parseInt(likeBtn.dataset.baseLikes || '0', 10);
      const liked = likeBtn.classList.contains('is-liked');
      countEl.textContent = String(base + (liked ? 1 : 0));
      return;
    }

    if (target === this.#dialog?.querySelector('.omerella-reel-modal__share-panel')) {
      this.#hideShare();
    }
  };

  #dismissBubble() {
    this.#bubble?.classList.add('is-dismissed');
    this.#preview?.pause();
  }

  #setupPreviewAutoplay() {
    if (!this.#preview || this.#bubble?.classList.contains('is-dismissed') || this.#dialog?.open) return;

    this.#preview.muted = true;
    this.#preview.defaultMuted = true;
    this.#preview.loop = true;
    this.#preview.playsInline = true;
    this.#preview.play().catch(() => {});
  }

  #resumePreview() {
    if (this.#bubble?.classList.contains('is-dismissed') || this.#dialog?.open) return;
    this.#preview?.play().catch(() => {});
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#dialog?.open) return;
    if (event.key === 'Escape') this.#close();
  };

  #open() {
    this.#bindDialog();
    if (!this.#dialog || !this.#reel) return;
    this.#portalDialog();
    this.#preview?.pause();
    this.#dialog.showModal();
    document.documentElement.classList.add('omerella-reel-modal-open');
    this.#setVideoSource(this.#video, this.#reel);
    this.#resetProgress();
    this.#playWhenReady(this.#video);
    this.#updateMuteIcon(true);
    this.#hideShare();
  }

  #close() {
    this.#dialog?.close();
    document.documentElement.classList.remove('omerella-reel-modal-open');
    this.#hideShare();
  }

  /**
   * @param {object} reel
   * @returns {string}
   */
  #resolveVideoUrl(reel) {
    if (!reel) return '';
    if (reel.mp4) return reel.mp4;

    const sources = reel.sources || [];
    const mp4 = sources.find((s) => s.url && (s.type?.includes('mp4') || s.url.toLowerCase().includes('.mp4')));
    if (mp4?.url) return mp4.url;

    return sources.find((s) => s.url)?.url || '';
  }

  /**
   * @param {HTMLVideoElement | null} videoEl
   * @param {object} reel
   */
  #setVideoSource(videoEl, reel) {
    if (!videoEl || !reel) return;

    const videoUrl = this.#resolveVideoUrl(reel);

    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.innerHTML = '';

    if (videoUrl) {
      videoEl.src = videoUrl;
    } else if (reel.sources?.length) {
      reel.sources.forEach((source) => {
        if (!source.url) return;
        const sourceEl = document.createElement('source');
        sourceEl.src = source.url;
        sourceEl.type = source.type || 'video/mp4';
        videoEl.appendChild(sourceEl);
      });
    }

    if (reel.poster) {
      videoEl.poster = reel.poster;
    } else {
      videoEl.removeAttribute('poster');
    }

    videoEl.muted = true;
    videoEl.defaultMuted = true;
    videoEl.loop = true;
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('webkit-playsinline', '');
    videoEl.preload = 'auto';
    videoEl.load();
  }

  /**
   * @param {HTMLVideoElement | null} videoEl
   */
  #playWhenReady(videoEl) {
    if (!videoEl) return;

    const attemptPlay = () => {
      if (!videoEl) return;
      videoEl.muted = true;
      videoEl.play().catch(() => {
        window.setTimeout(() => videoEl.play().catch(() => {}), 150);
      });
    };

    if (videoEl.readyState >= 2) {
      attemptPlay();
      return;
    }

    const onReady = () => attemptPlay();
    videoEl.addEventListener('loadeddata', onReady, { once: true });
    videoEl.addEventListener('canplay', onReady, { once: true });
  }

  #loadReelData() {
    const reel = this.#reel;
    if (!reel || !this.#dialog) return;

    const likeBtn = this.#dialog.querySelector('.omerella-reel-modal__like');
    if (likeBtn instanceof HTMLElement) {
      likeBtn.classList.remove('is-liked');
      likeBtn.dataset.baseLikes = String(reel.likes || 0);
      const countEl = likeBtn.querySelector('[data-like-count]');
      if (countEl) countEl.textContent = String(reel.likes || 0);
    }

    const shareBtn = this.#dialog.querySelector('.omerella-reel-modal__share-btn');
    if (shareBtn instanceof HTMLElement) {
      shareBtn.dataset.baseShares = String(reel.shares || 0);
      const shareCountEl = shareBtn.querySelector('[data-share-count]');
      if (shareCountEl) shareCountEl.textContent = String(reel.shares || 0);
    }
  }

  #onVariantUpdate = () => {
    const cartBtn = this.#dialog?.querySelector('.omerella-reel-modal__cart');
    const addBtn = document.querySelector('.omerella-product .add-to-cart-button');
    const priceEl = this.#dialog?.querySelector('.omerella-reel-modal__product-price');
    if (!(cartBtn instanceof HTMLButtonElement) || !this.#reel) return;

    const variantInput = document.querySelector('.omerella-product form input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : '';
    const soldOut = addBtn instanceof HTMLButtonElement && addBtn.disabled;
    const pagePrice = document.querySelector('.omerella-product product-price, .omerella-product .price');

    cartBtn.dataset.variantId = variantId;
    cartBtn.disabled = soldOut;
    cartBtn.textContent = soldOut
      ? this.dataset.soldOutLabel || 'SOLD OUT'
      : this.dataset.addToCartLabel || 'ADD TO CART';

    if (priceEl && pagePrice) {
      priceEl.innerHTML = pagePrice.innerHTML;
    }

    if (variantId) this.#reel.variantId = variantId;
    this.#reel.canAddToCart = !soldOut;
  };

  #resetProgress() {
    if (this.#progressBar) {
      this.#progressBar.style.width = '0%';
    }
  }

  #updateProgress() {
    if (!this.#video || !this.#progressBar) return;
    const { currentTime, duration } = this.#video;
    if (!duration || Number.isNaN(duration)) return;
    this.#progressBar.style.width = `${(currentTime / duration) * 100}%`;
  }

  #stopModalVideo() {
    if (!this.#video) return;
    this.#video.pause();
    this.#video.removeAttribute('src');
    this.#video.innerHTML = '';
    this.#video.load();
    this.#resetProgress();
  }

  #toggleMute() {
    if (!this.#video) return;
    const muted = !this.#video.muted;
    this.#video.muted = muted;
    this.#updateMuteIcon(muted);
  }

  /** @param {boolean} muted */
  #updateMuteIcon(muted) {
    const muteIcon = this.#dialog?.querySelector('.omerella-reel-modal__mute-icon--off');
    const unmuteIcon = this.#dialog?.querySelector('.omerella-reel-modal__mute-icon--on');
    muteIcon?.classList.toggle('hidden', !muted);
    unmuteIcon?.classList.toggle('hidden', muted);
  }

  /**
   * @param {Event} event
   * @param {HTMLButtonElement} btn
   */
  async #addToCart(event, btn) {
    event.preventDefault();
    const variantId = btn.dataset.variantId || this.#reel?.variantId;
    if (!variantId || btn.disabled) return;

    btn.disabled = true;
    const originalText = btn.textContent;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Could not add to cart');

      document.dispatchEvent(
        new CartAddEvent(data, this.id, {
          source: 'omerella-product-floating-video',
          variantId: String(variantId),
          productId: this.#reel?.productId,
        })
      );

      btn.textContent = this.dataset.addedLabel || 'ADDED';
      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    } catch {
      btn.textContent = this.dataset.errorLabel || 'ERROR';
      window.setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1800);
    }
  }

  #openWhatsApp() {
    const url = this.dataset.whatsappUrl;
    if (url) window.open(url, '_blank', 'noopener');
  }

  #toggleShare() {
    const panel = this.#dialog?.querySelector('.omerella-reel-modal__share-panel');
    panel?.classList.toggle('is-open');
  }

  #hideShare() {
    this.#dialog?.querySelector('.omerella-reel-modal__share-panel')?.classList.remove('is-open');
  }

  #shareWhatsApp() {
    if (!this.#reel?.productUrl) return;
    const text = encodeURIComponent(`${this.#reel.productTitle} ${this.#reel.productUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener');
    this.#hideShare();
  }

  async #copyLink() {
    if (!this.#reel?.productUrl) return;
    try {
      await navigator.clipboard.writeText(this.#reel.productUrl);
    } catch {
      /* ignore */
    }
    this.#hideShare();
  }

  #shareNative() {
    if (!this.#reel?.productUrl || !navigator.share) return;
    navigator
      .share({ title: this.#reel.productTitle, url: this.#reel.productUrl })
      .catch(() => {});
    this.#hideShare();
  }
}

if (!customElements.get('omerella-product-floating-video')) {
  customElements.define('omerella-product-floating-video', OmerellaProductFloatingVideo);
}
